import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { environment } from '@env/environment';
import { ApiService } from '@services/api.service';
import { enumHtsChatScope, enumSimilarHtsSuggestionScope } from '@data-layer/enums/common';
import {
  HtsChatAnswerModel,
  HtsChatApplicationModel,
  HtsChatConversationModel,
  HtsChatConversationsModel,
  HtsChatEntryModel,
  HtsChatStartModel,
  LineCatalogInsertModel,
  SimilarHtsConfirmModel,
  SimilarHtsSuggestionModel,
} from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { LineModel } from '@data-layer/models/entry-summaries';

/**
 * Classification assistant (#13383). Route families, one object:
 * - conversation routes — answer / restart a conversation by its id (a conversation lives on a line — Find HTS saves first — or,
 *   on the BINA Catalog page, on the company alone);
 * - company route — start a conversation without a line (#13554): the typed description is its first message;
 * - catalog item route — the conversations saved into a catalog item (its "History", #13554);
 * - line routes — read the line's history, start its conversation, apply a finished conversation's code;
 * - line verification routes — catalog insert (#13411), similar-product suggestions (#13517), save to catalog (#13407).
 * Errors are NOT routed to the global alert: every call rethrows so the drawer can show the tag inline — except the catalog
 * item history read, which has no drawer to report into (the global alert does).
 */
@Injectable({
  providedIn: 'root',
})
export class HtsChatRepository {
  constructor(private apiService: ApiService) {}

  // ---- conversation routes ------------------------------------------------------------------------

  public getConversation(conversationId: number): Observable<HtsChatConversationModel> {
    return this.apiService.get<HtsChatConversationModel>(
      `${this.conversationPath(conversationId)}`,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  public answerConversation(conversationId: number, answer: string): Observable<HtsChatConversationModel> {
    const body: HtsChatAnswerModel = { answer };
    return this.apiService.post<HtsChatConversationModel>(
      `${this.conversationPath(conversationId)}/answers`,
      body,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /** Ends the conversation (it stays in the history) and returns the NEW one — switch to its id. */
  public restartConversation(conversationId: number): Observable<HtsChatConversationModel> {
    return this.apiService.post<HtsChatConversationModel>(
      `${this.conversationPath(conversationId)}/restarts`,
      null,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  // ---- company route (BINA Catalog, #13554) --------------------------------------------------------

  /** Starts a conversation that belongs to the company only (no line): `messages[0]` is the product description. */
  public startForCompany(companyId: number, messages: string[]): Observable<HtsChatConversationModel> {
    const body: HtsChatStartModel = { messages };
    return this.apiService.post<HtsChatConversationModel>(
      `${environment.url.companies}${companyId}/hts-chats`,
      body,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  // ---- catalog item route (BINA Catalog history, #13554) -------------------------------------------

  /** Every conversation attached to a catalog item (`isFromAssistant` says there is at least one); the global loader shows. */
  public getCatalogItemConversations(favoriteHtsNumberId: number): Observable<HtsChatConversationModel[]> {
    return this.apiService.get<HtsChatConversationModel[]>(
      `${environment.url.prefixCustoms}v2/favorite-hts-numbers/${favoriteHtsNumberId}/hts-chats`,
    );
  }

  // ---- entry routes (history, #13385) --------------------------------------------------------------

  /** Every conversation of the entry grouped by line; the endpoint family follows the caller's role. */
  public getEntryHistory(entrySummaryId: number, scope: enumHtsChatScope): Observable<HtsChatEntryModel> {
    return this.apiService.get<HtsChatEntryModel>(
      this.entryChatPath(entrySummaryId, scope),
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  // ---- line routes -------------------------------------------------------------------------------

  /** Every conversation the line ever had, oldest first. Broker scope reads through the management prefix. */
  public getLineConversations(lineId: number, scope: enumHtsChatScope): Observable<HtsChatConversationsModel> {
    return this.apiService.get<HtsChatConversationsModel>(
      `${this.lineChatPath(lineId, scope)}`,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /**
   * Starts the line's conversation from its STORED description, or resumes the active one (idempotent, no body).
   * Returns the line's whole history; the new conversation is linked to the line from the start (#13383 F6).
   */
  public startForLine(lineId: number): Observable<HtsChatConversationsModel> {
    return this.apiService.post<HtsChatConversationsModel>(
      this.lineChatPath(lineId, enumHtsChatScope.Importer),
      null,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /** Writes the conversation's code onto the line (separators stripped) AND verifies it in one call. */
  public applyToLine(lineId: number, conversationId: number): Observable<LineModel> {
    const body: HtsChatApplicationModel = { conversationId };
    return this.apiService.post<LineModel>(
      `${this.lineChatPath(lineId, enumHtsChatScope.Importer)}/applications`,
      body,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  // ---- line verification routes ----------------------------------------------------------------

  /**
   * Saves the conversation to the BINA catalog: attaches it to the company's oldest item for the code or creates one titled
   * with the conversation's description. Idempotent — calling it again returns the same item.
   */
  public saveToCatalog(conversationId: number): Observable<FavoriteHtsNumberModel> {
    return this.apiService.post<FavoriteHtsNumberModel>(
      `${this.conversationPath(conversationId)}/catalog-item`,
      null,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /**
   * Writes a catalog item's code onto a saved line (the description stays). Validates the line as `Catalog` only when the item
   * carries a conversation about the same product; otherwise the line still needs classification.
   */
  public insertCatalogItem(lineId: number, favoriteHtsNumberId: number): Observable<LineModel> {
    const body: LineCatalogInsertModel = { favoriteHtsNumberId };
    return this.apiService.post<LineModel>(
      `${this.linePath(lineId)}/catalog-insertions`,
      body,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /** Codes of similar products the company already classified, for a SAVED line (catalog, earlier chats, released entries). */
  public getLineSuggestions(lineId: number): Observable<SimilarHtsSuggestionModel[]> {
    return this.apiService.get<SimilarHtsSuggestionModel[]>(
      `${this.linePath(lineId)}/similar-hts-suggestions`,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  /**
   * Free-text variant (≤1000 chars). Without `scope` it searches the company's assistant-backed catalog items only; `All`
   * searches every source of the line endpoint (#13534). `editedLineId` — the saved line being edited: it is left out of the
   * suggestions together with the code it is verified with (a foreign / ISF line → 404, handled as an empty list).
   */
  public searchCompanySuggestions(
    companyId: number,
    text: string,
    scope: enumSimilarHtsSuggestionScope | null = null,
    editedLineId: number | null = null,
  ): Observable<SimilarHtsSuggestionModel[]> {
    const params: { [key: string]: string | number } = { text };
    if (scope) {
      params.scope = scope;
    }
    if (editedLineId !== null) {
      params.editedLineId = editedLineId;
    }
    return this.apiService.get<SimilarHtsSuggestionModel[]>(
      `${environment.url.companies}${companyId}/similar-hts-suggestions`,
      { params },
      { isShowLoader: false },
      this.silentError,
    );
  }

  /** Confirms a suggestion: writes only the code and verifies the line as `SimilarityConfirmed`. */
  public confirmSuggestion(lineId: number, body: SimilarHtsConfirmModel): Observable<LineModel> {
    return this.apiService.post<LineModel>(
      `${this.linePath(lineId)}/similar-hts-confirmations`,
      body,
      null,
      { isShowLoader: false },
      this.silentError,
    );
  }

  // ---- paths — the only place URLs are built ----------------------------------------------------

  private conversationPath(conversationId: number): string {
    return `${environment.url.prefixCustoms}v2/hts-chats/${conversationId}`;
  }

  private entryChatPath(entrySummaryId: number, scope: enumHtsChatScope): string {
    const prefix: string =
      scope === enumHtsChatScope.Admin
        ? `${environment.url.prefixCustoms}v2/management/entry-summaries/`
        : scope === enumHtsChatScope.Broker
        ? `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/`
        : `${environment.url.prefixCustoms}v2/entry-summaries/`;
    return `${prefix}${entrySummaryId}/hts-chats`;
  }

  private linePath(lineId: number): string {
    return `${environment.url.prefixCustoms}v2/line-items/${lineId}`;
  }

  private lineChatPath(lineId: number, scope: enumHtsChatScope): string {
    const prefix: string =
      scope === enumHtsChatScope.Broker
        ? `${environment.url.prefixCustoms}v2/management/customs-broker/line-items/`
        : `${environment.url.prefixCustoms}v2/line-items/`;
    return `${prefix}${lineId}/hts-chat`;
  }

  private readonly silentError = (err: HttpErrorResponse): Observable<never> => throwError(err);
}
